import type { TitleOption } from '../../profile/profile-assets.js';

const NONE_VALUE = '__none__';

export interface TitleSelectorProps {
  options: readonly TitleOption[];
  value: string | null;
  onChange: (next: string | null) => void;
}

/**
 * The Title picker (APP_SPEC §13.2). Text-only by design — unlike the other
 * three cosmetic slots there is no artwork to preview, so `tablet:` and up
 * keeps the existing gold-selected pill row. Below `tablet:` a wrap of up to
 * nine pills was the thing being replaced, so it becomes a single styled
 * native `<select>` instead — matching the app's existing styled-select
 * convention (`AdminPage.tsx`'s status select) rather than inventing a new
 * listbox component. Locked titles stay filtered out entirely in both modes,
 * as they were before this redesign.
 */
export function TitleSelector({ options, value, onChange }: TitleSelectorProps) {
  const selectable = options.filter((option) => option.unlocked);

  return (
    <fieldset className="min-w-0">
      <legend className="text-sm font-semibold text-textPrimary">Title</legend>

      <div className="mt-2 tablet:hidden">
        <select
          aria-label="Title"
          className="min-h-touch w-full rounded-md border border-border bg-parchment px-3 py-2 text-textPrimary shadow-inset"
          value={value ?? NONE_VALUE}
          onChange={(event) => {
            const next = event.target.value;
            onChange(next === NONE_VALUE ? null : next);
          }}
        >
          <option value={NONE_VALUE}>None</option>
          {selectable.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-2 hidden flex-wrap gap-2 tablet:flex">
        <TitlePillButton label="None" selected={value === null} onClick={() => onChange(null)} />
        {selectable.map((option) => (
          <TitlePillButton
            key={option.id}
            label={option.name}
            selected={value === option.id}
            onClick={() => onChange(option.id)}
          />
        ))}
      </div>
    </fieldset>
  );
}

function TitlePillButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`min-h-touch rounded-md border px-3 py-2 text-sm font-semibold transition-colors duration-fast ${
        selected
          ? 'border-accent bg-brand text-textOnBrand shadow-1'
          : 'border-border bg-surface text-textPrimary hover:bg-surfaceElevated'
      }`}
    >
      {label}
    </button>
  );
}
